import { ChangeEvent, useEffect, useState } from 'react';

import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { isAxiosError } from 'axios';
import IntlTelInput from 'intl-tel-input/reactWithUtils';
import 'intl-tel-input/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  UniversignSauvegardeFileRequest,
  UniversignSauvegardeFileResponse,
  UniversignValidationCoordonneesRequestSource,
} from '@/api/placementApi.schemas';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import FormTextError from '@/components/formTextError/formTextError';
import FormTextSuccess from '@/components/formTextSuccess/formTextSuccess';
import InputFile from '@/components/inputFile/inputFile';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import TextInput from '@/components/TextInput/TextInput';
import { ROUTE } from '@/constants/route';
import {
  CODE_CHECK_ERROR_KEY_BY_MESSAGE,
  CODE_CHECK_ERROR_KEY_BY_STATUS,
  CODE_CHECK_GENERIC_ERROR_KEY,
  CODE_CHECK_UNKNOWN_SOUSCRIPTION_STATUS,
} from '@/constants/universignErrors';
import { COORDONNEES_CHECK_CHANNEL, COORDONNEES_CHECK_STATUS } from '@/enums/coordonneesCheck';
import { useAuth } from '@/hooks/useAuth';
import { useFaireVersementJustificatifsCodeEmail } from '@/hooks/useFaireVersementJustificatifsCodeEmail';
import { useFaireVersementJustificatifsCoordonnees } from '@/hooks/useFaireVersementJustificatifsCoordonnees';
import { useRappelConseiller } from '@/hooks/useRappelConseiller';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ModalRappel } from '@/modals/ContentModalRappel/ContentModalRappel';
import {
  modalCniContent,
  modalPasseportContent,
  modalTelephoneContent,
} from '@/modals/ContentModalVersementJustificatifs/ContentModalVersementJustificatifs';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import base64ToBlob from '@/utils/functions/base64ToBlob/base64ToBlob';
import getUniversignReadingErrorMessageId from '@/utils/functions/getUniversignReadingErrorMessageId/getUniversignReadingErrorMessageId';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

interface JustificatifsFilesType {
  idSouscription: number;
  typeDocumentIdentite: string;
  justificatifIdentiteRecto: string;
  justificatifIdentiteVerso: string;
  justificatifTelephone: string;
  justificatifOrigineFonds: string;
}

interface ValidationUniversignResult {
  success: boolean;
  pieceLu?: boolean;
  pieceMRZ?: boolean;
  infosNonCorrespondante?: boolean;
  pieceExpire?: boolean;
  coordonneesUtilise?: boolean;
  emailUtilise?: string | null;
  telephoneUtilise?: string | null;
  birthdate?: string;
  reasonMessage?: string | null;
}

interface UniversignErrorState {
  success?: boolean;
  emailUtilise?: string | null;
  telephoneUtilise?: string | null;
  coordonneesUtilise?: boolean;
  pieceLu?: boolean;
  infosNonCorrespondante?: boolean;
  pieceExpire?: boolean;
}

type MutationHook<TData, TVariables> = {
  mutate: (
    variables: TVariables,
    options?: {
      onSuccess?: (data: TData) => void;
      onError?: (error: unknown) => void;
    }
  ) => void;
};

interface UniversignDocumentsFormProps {
  idSouscription: number;
  sessionStorageDataKey: string;
  coordonneesSource: NonNullable<UniversignValidationCoordonneesRequestSource>;
  universignError?: UniversignErrorState | null;
  validFile?: Record<string, boolean | undefined>;
  breadcrumbItems: BreadcrumbItem[];
  showOrigineFonds?: boolean;
  showTelephone?: boolean;
  origineCapitaux?: string;
  defaultDocumentType?: 'cni' | 'passeport' | 'carte-de-sejour';
  sauvegarderMutation: MutationHook<UniversignSauvegardeFileResponse, { data: UniversignSauvegardeFileRequest }>;
  validationMutation?: MutationHook<ValidationUniversignResult, number>;
  onSuccess?: (data: ValidationUniversignResult) => void;
}

const emptyFile = { name: '', size: 0, symbol: '', extension: '', url: '' };
const emptyParcoursData = {
  'justificatif-identite-1-recto': emptyFile,
  'justificatif-identite-1-verso': emptyFile,
  'justificatif-telephone': emptyFile,
  'justificatif-origine_fonds': emptyFile,
};
const TAILLE_MAX_MO = 10;
const FORMATS_VALIDES = ['PDF', 'JPG', 'JPEG', 'PNG', 'TIFF'];
const DOT = '.';

// Universign peut remonter plusieurs erreurs à la fois : seule la plus prioritaire est conservée (pièce illisible
// ou MRZ, pièce expirée, infos non correspondantes, puis coordonnées déjà utilisées : email et téléphone, email, téléphone).
const keepPriorityUniversignError = (error?: UniversignErrorState | null): UniversignErrorState | null | undefined => {
  if (!error) return error;
  if (error.pieceLu === false) return { success: error.success, pieceLu: false };
  if (error.pieceExpire === true) return { success: error.success, pieceExpire: true };
  if (error.infosNonCorrespondante === true) return { success: error.success, infosNonCorrespondante: true };
  return error;
};

const UniversignDocumentsForm: React.FC<UniversignDocumentsFormProps> = ({
  idSouscription,
  sessionStorageDataKey,
  coordonneesSource,
  universignError: universignErrorProp,
  validFile = {},
  breadcrumbItems,
  showOrigineFonds = false,
  showTelephone = true,
  origineCapitaux,
  defaultDocumentType = 'cni',
  sauvegarderMutation,
  validationMutation = { mutate: (_, options) => options?.onSuccess?.({ success: true }) },
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { windowWidth } = useScreenSize();
  const coordonneesMutation = useFaireVersementJustificatifsCoordonnees();
  const codeEmailMutation = useFaireVersementJustificatifsCodeEmail();

  const DOCUMENT_OPTIONS = [
    { value: 'passeport', label: t('justificatifsUniversign.documentTypePasseport') },
    { value: 'cni', label: t('justificatifsUniversign.documentTypeCni') },
    { value: 'carte-de-sejour', label: t('justificatifsUniversign.documentTypeCarteDeSejour') },
  ];

  const parcoursData = JSON.parse(sessionStorage.getItem(sessionStorageDataKey) ?? 'null') ?? emptyParcoursData;

  const [showLoader, setShowLoader] = useState(false);

  const universignError = keepPriorityUniversignError(universignErrorProp);

  let initialEmailTelUniversign = 'y**@g**.**';
  if (universignError?.emailUtilise) {
    initialEmailTelUniversign = universignError.emailUtilise;
  } else if (universignError?.telephoneUtilise) {
    initialEmailTelUniversign = universignError.telephoneUtilise;
  }
  const [emailTelUniversign, setEmailTelUniversign] = useState(initialEmailTelUniversign);

  const [universignCoordonnees, setUniversignCoordonnees] = useState({
    idSouscription,
    email: '',
    telephone: '',
    code: '',
  });
  const [birthdate, setBirthdate] = useState('');

  const [origineFondsLabel, setOrigineFondsLabel] = useState('');
  const [origineFondsInfo, setOrigineFondsInfo] = useState('');

  const [showModalTelephone, setShowModalTelephone] = useState(false);
  const [showModalBonnesPratiquesCni, setShowModalBonnesPratiquesCni] = useState(false);
  const [showModalBonnesPratiquesPasseport, setShowModalBonnesPratiquesPasseport] = useState(false);
  const [showModalCodeEmail, setShowModalCodeEmail] = useState(false);
  const [showModalRappel, setShowModalRappel] = useState(false);

  const [showTextCodeEmailResend, setShowTextCodeEmailResend] = useState(false);

  const [submitButtonLabel, setSubmitButtonLabel] = useState(
    universignError?.emailUtilise ? t('common.validateButton') : t('justificatifsUniversign.goToSignature')
  );
  const [showContactConseillerButton, setShowContactConseillerButton] = useState(
    universignError?.coordonneesUtilise === true &&
      universignError?.emailUtilise === null &&
      universignError?.telephoneUtilise === null
  );

  const [showContactAdvisorFallback, setShowContactAdvisorFallback] = useState(false);

  const [missingValue, setMissingValue] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const [errorJustificatif, setErrorJustificatif] = useState(universignError?.success === false);
  const [displayErrorJustificatif, setDisplayErrorJustificatif] = useState(false);
  // Message produit de l'erreur de lecture Universign ; null tant que SEL ne remonte pas le motif (reasonMessage absent).
  const [readingErrorMessageId, setReadingErrorMessageId] = useState<string | null>(null);

  const [erreurCompression, setErreurCompression] = useState({
    'justificatif-identite-1-recto': false,
    'justificatif-identite-1-verso': false,
    'justificatif-telephone': false,
    'justificatif-origine_fonds': false,
  });

  const [messageErreurFichier, setMessageErreurFichier] = useState<Record<string, string>>({
    'justificatif-identite-1-recto': '',
    'justificatif-identite-1-verso': '',
    'justificatif-telephone': '',
    'justificatif-origine_fonds': '',
  });

  const [isFormValid, setIsFormValid] = useState({
    justificatifIdentiteRecto: true,
    justificatifIdentiteVerso: true,
    justificatifTelephone: true,
    justificatifOrigineFonds: true,
  });

  const [versementJustificatifError, setVersementJustificatifError] = useState({
    pieceLu: universignError?.pieceLu === false,
    infosNonCorrespondante: universignError?.infosNonCorrespondante === true,
    pieceExpire: universignError?.pieceExpire === true,
    coordonneesUtilisees: {
      mail: !!(
        universignError?.coordonneesUtilise === true &&
        (universignError?.emailUtilise !== null ||
          (universignError?.emailUtilise === null && universignError?.telephoneUtilise === null))
      ),
      phone: !!(
        universignError?.coordonneesUtilise === true &&
        (universignError?.telephoneUtilise !== null ||
          (universignError?.emailUtilise === null && universignError?.telephoneUtilise === null))
      ),
    },
  });

  const [universignTelMailError, setUniversignTelMailError] = useState(false);
  const [universignTelMailErrorText, setUniversignTelMailErrorText] = useState('');
  const [coordonneesCheckErrorText, setCoordonneesCheckErrorText] = useState('');
  const [universignCodeError, setUniversignCodeError] = useState(false);
  const [universignCodeErrorText, setUniversignCodeErrorText] = useState('');
  const [universignCodeSouscriptionError, setUniversignCodeSouscriptionError] = useState(false);

  let initialTypeDocumentIdentite: string = parcoursData.typeIdentite?.toLowerCase() ?? defaultDocumentType;

  if (
    (validFile['justificatif-identite-1'] === true && universignError?.success !== false) ||
    !parcoursData.typeIdentite
  ) {
    initialTypeDocumentIdentite = defaultDocumentType;
  }

  const [versementJustificatifsData, setVersementJustificatifsData] = useState<JustificatifsFilesType>({
    idSouscription,
    typeDocumentIdentite: initialTypeDocumentIdentite,
    justificatifIdentiteRecto: parcoursData['justificatif-identite-1-recto']?.name ?? '',
    justificatifIdentiteVerso: parcoursData['justificatif-identite-1-verso']?.name ?? '',
    justificatifTelephone: parcoursData['justificatif-telephone']?.name ?? '',
    justificatifOrigineFonds: parcoursData['justificatif-origine_fonds']?.name ?? '',
  });

  const [justificatifsUrl, setJustificatifsUrl] = useState({
    justificatifIdentiteRectoURL: parcoursData['justificatif-identite-1-recto']?.url ?? '',
    justificatifIdentiteversoURL: parcoursData['justificatif-identite-1-verso']?.url ?? '',
    justificatifTelephoneURL: parcoursData['justificatif-telephone']?.url ?? '',
    justificatifOrigineFondsURL: parcoursData['justificatif-origine_fonds']?.url ?? '',
  });

  const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);

  const rappel = useRappelConseiller();
  const openModalRappel = () => {
    rappel.mutate();
    openModal('showModalRappel');
  };

  let errorMessageIdentiteRecto = '';
  if (displayErrorJustificatif) {
    errorMessageIdentiteRecto = t('justificatifsUniversign.fileRejectedError');
  } else if (erreurCompression['justificatif-identite-1-recto']) {
    errorMessageIdentiteRecto =
      messageErreurFichier['justificatif-identite-1-recto'] || t('justificatifsUniversign.fileUploadError');
  }

  let errorMessageIdentiteVerso = '';
  if (displayErrorJustificatif) {
    errorMessageIdentiteVerso = t('justificatifsUniversign.fileRejectedError');
  } else if (erreurCompression['justificatif-identite-1-verso']) {
    errorMessageIdentiteVerso =
      messageErreurFichier['justificatif-identite-1-verso'] || t('justificatifsUniversign.fileUploadError');
  }

  const showCoordonneesCheckContactAdvisor = () => {
    closeModal();
    setVersementJustificatifError({
      pieceLu: false,
      infosNonCorrespondante: false,
      pieceExpire: false,
      coordonneesUtilisees: { mail: true, phone: true },
    });
    setShowContactConseillerButton(true);
    setShowContactAdvisorFallback(true);
  };

  const handleSendVerificationCoordonnees = async (isResend: boolean) => {
    setCoordonneesCheckErrorText('');
    globalThis.scrollTo({ top: 0, behavior: 'smooth' });

    const { channel, status, messageKey } = await coordonneesMutation.checkCoordonnees({
      ...universignCoordonnees,
      source: coordonneesSource,
    });

    // Un téléphone accepté est enregistré tout de suite : aucun code à saisir.
    if (status === COORDONNEES_CHECK_STATUS.VALID && channel === COORDONNEES_CHECK_CHANNEL.TELEPHONE) {
      sendToUniverSign();
      return;
    }

    setShowLoader(false);

    if (status === COORDONNEES_CHECK_STATUS.VALID) {
      if (isResend) {
        setUniversignCoordonnees((current) => ({ ...current, code: '' }));
      }
      setUniversignCodeError(false);
      setUniversignCodeSouscriptionError(false);
      setShowTextCodeEmailResend(isResend);
      openModal('showModalCodeEmail');
      return;
    }

    if (isResend) {
      setShowTextCodeEmailResend(false);
      setUniversignCodeSouscriptionError(false);
      setUniversignCodeError(true);
      setUniversignCodeErrorText(t('justificatifsUniversign.emailVerificationResendError'));
      return;
    }

    if (messageKey !== undefined) {
      setCoordonneesCheckErrorText(t(messageKey));
      return;
    }

    showCoordonneesCheckContactAdvisor();
  };

  const handleSubmitCodeVerification = (e: React.FormEvent) => {
    e.preventDefault();
    setUniversignCodeError(false);
    setUniversignCodeSouscriptionError(false);
    setShowTextCodeEmailResend(false);

    if (universignCoordonnees.code === '') {
      setUniversignCodeError(true);
      setUniversignCodeErrorText(t('justificatifsUniversign.requiredField'));
      globalThis.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setShowLoader(true);

    codeEmailMutation.mutate(universignCoordonnees, {
      onSuccess: (data) => {
        setShowLoader(false);
        if (data.valide) {
          setUniversignCodeError(false);
          sendToUniverSign();
          return;
        }

        const messageKey =
          (data.message && CODE_CHECK_ERROR_KEY_BY_MESSAGE[data.message]) || CODE_CHECK_GENERIC_ERROR_KEY;
        setUniversignCodeError(true);
        setUniversignCodeErrorText(t(messageKey));
      },
      onError: (error) => {
        setShowLoader(false);
        log.error(`codeEmailMutation failed: ${error}`);
        const status = isAxiosError(error) ? error.response?.status : undefined;

        if (status === CODE_CHECK_UNKNOWN_SOUSCRIPTION_STATUS) {
          setUniversignCodeError(true);
          setUniversignCodeErrorText('');
          setUniversignCodeSouscriptionError(true);
          return;
        }

        const messageKey =
          status === undefined
            ? CODE_CHECK_GENERIC_ERROR_KEY
            : (CODE_CHECK_ERROR_KEY_BY_STATUS[status] ?? CODE_CHECK_GENERIC_ERROR_KEY);
        setUniversignCodeError(true);
        setUniversignCodeErrorText(t(messageKey));
      },
    });
  };

  const modalCodeEmailContent = (
    <>
      {showTextCodeEmailResend && (
        <div className={styles.resendEmailConfirmation}>
          <FormTextSuccess text={t('justificatifsUniversign.emailVerificationCodeResent')} />
        </div>
      )}
      <p>
        {t('justificatifsUniversign.emailVerificationIntro')}{' '}
        <span className={`bolder-text ${styles.universignErrorEmail}`}>{universignCoordonnees.email}</span>
      </p>

      <form
        onSubmit={(e) => {
          handleSubmitCodeVerification(e);
        }}
      >
        <TextInput
          label={t('justificatifsUniversign.emailVerificationCodeLabel')}
          labelFor="universign-email-code"
          placeholder={t('justificatifsUniversign.emailVerificationCodePlaceholder')}
          type="text"
          value={universignCoordonnees.code}
          isError={universignCodeError}
          textError={universignCodeErrorText}
          onChange={(e) => {
            setUniversignCoordonnees({ ...universignCoordonnees, code: e.currentTarget.value });
            setUniversignCodeError(false);
            setUniversignCodeSouscriptionError(false);
          }}
        />

        {universignCodeError && universignCodeSouscriptionError && (
          <FormTextError
            text={
              <>
                {t('justificatifsUniversign.unknownSouscription')}{' '}
                <button
                  className={styles.formErrorPhone}
                  type="button"
                  onClick={() => handlePhoneCall()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePhoneCall();
                    }
                  }}
                  tabIndex={0}
                >
                  {t('helpContact.tel')}
                </button>
                {DOT}
              </>
            }
          />
        )}

        <input className="hide-element" type="submit" />
      </form>

      <button
        className={styles.resendCode}
        onClick={() => {
          setShowLoader(true);
          handleSendVerificationCoordonnees(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowLoader(true);
            handleSendVerificationCoordonnees(true);
          }
        }}
      >
        {t('justificatifsUniversign.emailVerificationResend')}
      </button>
    </>
  );

  const OnChangePhoneInputError = (value: string) => {
    if (value !== universignCoordonnees.telephone) {
      setUniversignCoordonnees({ ...universignCoordonnees, telephone: value });
      setCoordonneesCheckErrorText('');
    }
  };

  const setJustificatifsUrlFunction = (whichFile: string, blobUrl: string) => {
    switch (whichFile) {
      case 'justificatif-identite-1-recto':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifIdentiteRectoURL: blobUrl });
        break;
      case 'justificatif-identite-1-verso':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifIdentiteversoURL: blobUrl });
        break;
      case 'justificatif-telephone':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifTelephoneURL: blobUrl });
        break;
      case 'justificatif-origine_fonds':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifOrigineFondsURL: blobUrl });
        break;
    }
  };

  const sendFile = (base64: string, whichFile: string, file: File) => {
    const dataToSend: JustificatifsFilesType = {
      idSouscription: versementJustificatifsData.idSouscription,
      typeDocumentIdentite: '',
      justificatifIdentiteRecto: '',
      justificatifIdentiteVerso: '',
      justificatifTelephone: '',
      justificatifOrigineFonds: '',
    };

    switch (whichFile) {
      case 'justificatif-identite-1-recto':
        dataToSend.typeDocumentIdentite = versementJustificatifsData.typeDocumentIdentite;
        dataToSend.justificatifIdentiteRecto = base64;
        break;
      case 'justificatif-identite-1-verso':
        dataToSend.typeDocumentIdentite = versementJustificatifsData.typeDocumentIdentite;
        dataToSend.justificatifIdentiteVerso = base64;
        break;
      case 'justificatif-telephone':
        dataToSend.justificatifTelephone = base64;
        break;
      case 'justificatif-origine_fonds':
        dataToSend.justificatifOrigineFonds = base64;
        break;
    }

    sauvegarderMutation.mutate(
      {
        data: {
          idSouscription: dataToSend.idSouscription,
          typeDocumentIdentite: dataToSend.typeDocumentIdentite || null,
          'justificatif-identite-1-recto': dataToSend.justificatifIdentiteRecto || null,
          'justificatif-identite-1-verso': dataToSend.justificatifIdentiteVerso || null,
          'justificatif-telephone': dataToSend.justificatifTelephone || null,
        },
      },
      {
        onSuccess: (response) => {
          setDisableSubmitBtn(false);

          if (!response?.success) {
            setErreurCompression({ ...erreurCompression, [whichFile]: true });
            setMessageErreurFichier({ ...messageErreurFichier, [whichFile]: response?.message ?? '' });
            log.error(`Echec de la sauvegarde de ${whichFile} : ${response?.message ?? 'aucun message'}`);
            return;
          }

          const preview = response.preview ?? '';
          const extensionTmp = file.name.split('.').pop() || '';
          const blobUrl = URL.createObjectURL(base64ToBlob(preview, 'image/jpeg'));

          setJustificatifsUrlFunction(whichFile, blobUrl);

          let tempSize = (file.size / 1000000).toFixed(2);
          if (file.size < 1000) {
            tempSize = file.size.toFixed(2);
          } else if (file.size < 1000000) {
            tempSize = (file.size / 1000).toFixed(2);
          }

          let tempSymbol = '';
          if (file.size < 1000) {
            tempSymbol = 'o';
          } else if (file.size < 1000000) {
            tempSymbol = 'Ko';
          } else {
            tempSymbol = 'Mo';
          }

          const currentData = JSON.parse(sessionStorage.getItem(sessionStorageDataKey) ?? 'null') ?? emptyParcoursData;
          sessionStorage.setItem(
            sessionStorageDataKey,
            JSON.stringify({
              ...currentData,
              [whichFile]: {
                ...currentData[whichFile],
                name: file.name,
                size: tempSize,
                symbol: tempSymbol,
                extension: extensionTmp?.toUpperCase(),
                url: blobUrl,
              },
            })
          );
        },
        onError: (error) => {
          setDisableSubmitBtn(false);
          log.error(`Erreur lors de l'envoi du fichier ${whichFile} : ${error}`);
        },
      }
    );
  };

  const getBase64 = (file: File) => {
    return new Promise((resolve) => {
      let baseURL: string | ArrayBuffer | null = '';
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        baseURL = reader.result;
        resolve(baseURL);
      };
    });
  };

  const convertToBase64 = (whichFile: string, file: File) => {
    getBase64(file)
      .then((result: unknown) => {
        if (typeof result !== 'string') {
          log.error(`Le résultat de la conversion base64 n'est pas une string pour ${whichFile}`);
          return;
        }

        const result64 = result.split('base64,')[1];
        const extensionTmp = file.name.split('.').pop() || '';

        switch (whichFile) {
          case 'justificatif-identite-1-recto':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifIdentiteRecto: result64 });
            break;
          case 'justificatif-identite-1-verso':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifIdentiteVerso: result64 });
            break;
          case 'justificatif-telephone':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifTelephone: result64 });
            break;
          case 'justificatif-origine_fonds':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifOrigineFonds: result64 });
            break;
        }

        if (file.size < TAILLE_MAX_MO * 1000000 && FORMATS_VALIDES.includes(extensionTmp.toUpperCase())) {
          if (result64 !== '') {
            sendFile(result64, whichFile, file);
          }
        } else {
          setJustificatifsUrlFunction(whichFile, URL.createObjectURL(file));
          setDisableSubmitBtn(false);
        }
      })
      .catch((err) => {
        log.error(`Erreur lors de la conversion du fichier ${whichFile} en base64 : ${err}`);
      });
  };

  const setFile = (whichFile: string, file: File) => {
    setDisplayErrorJustificatif(false);
    setMissingValue(false);

    setErreurCompression({
      ...erreurCompression,
      [whichFile]: false,
    });

    setMessageErreurFichier({
      ...messageErreurFichier,
      [whichFile]: '',
    });

    if (file.size !== 0) setDisableSubmitBtn(true);

    convertToBase64(whichFile, file);
  };

  const handleFileValidity = (whichFile: string, isValid: boolean) => {
    switch (whichFile) {
      case 'justificatif-identite-1-recto':
        setIsFormValid({ ...isFormValid, justificatifIdentiteRecto: isValid });
        break;
      case 'justificatif-identite-1-verso':
        setIsFormValid({ ...isFormValid, justificatifIdentiteVerso: isValid });
        break;
      case 'justificatif-telephone':
        setIsFormValid({ ...isFormValid, justificatifTelephone: isValid });
        break;
      case 'justificatif-origine_fonds':
        setIsFormValid({ ...isFormValid, justificatifOrigineFonds: isValid });
        break;
    }
  };

  const handleErrorBreadcrumbs = () => {
    if (
      (universignError?.emailUtilise && universignError?.telephoneUtilise === null) ||
      (universignError?.emailUtilise && universignError?.telephoneUtilise)
    ) {
      globalThis.history.go(-1);
    }
    setUniversignTelMailError(false);
    setUniversignCoordonnees({ ...universignCoordonnees, email: '', telephone: '', code: '' });
    setMissingValue(false);

    setShowContactConseillerButton(false);
    setSubmitButtonLabel(t('justificatifsUniversign.goToSignature'));

    setErrorJustificatif(false);

    setIsFormValid({
      ...isFormValid,
      justificatifTelephone: true,
    });

    setVersementJustificatifError({
      pieceLu: false,
      infosNonCorrespondante: false,
      pieceExpire: false,
      coordonneesUtilisees: {
        mail: false,
        phone: false,
      },
    });
  };

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    switch (modal) {
      case 'showModalTelephone':
        setShowModalTelephone(true);
        break;
      case 'showModalBonnesPratiquesCni':
        setShowModalBonnesPratiquesCni(true);
        break;
      case 'showModalBonnesPratiquesPasseport':
        setShowModalBonnesPratiquesPasseport(true);
        break;
      case 'showModalCodeEmail':
        setShowModalCodeEmail(true);
        break;
      case 'showModalRappel':
        setShowModalRappel(true);
        break;
      default:
        break;
    }
  };

  const closeModal = () => {
    document.body.classList.remove('modal-open');
    setShowModalTelephone(false);
    setShowModalBonnesPratiquesCni(false);
    setShowModalBonnesPratiquesPasseport(false);
    setShowModalCodeEmail(false);
    setUniversignCodeError(false);
    setShowTextCodeEmailResend(false);
    setShowModalRappel(false);
  };

  const openBonnesPratiquesModale = () => {
    switch (versementJustificatifsData.typeDocumentIdentite) {
      case 'passeport':
        openModal('showModalBonnesPratiquesPasseport');
        break;
      case 'cni':
      default:
        openModal('showModalBonnesPratiquesCni');
        break;
    }
  };

  const scrollToInputError = (anchorId: string) => {
    const anchor = document.getElementById(anchorId);
    anchor?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePhoneCall = () => {
    globalThis.location.href = 'tel:+900300400';
  };

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setMissingValue(false);

    const currentData = JSON.parse(sessionStorage.getItem(sessionStorageDataKey) ?? 'null') ?? emptyParcoursData;

    if (e.target.value === 'passeport') {
      sessionStorage.setItem(
        sessionStorageDataKey,
        JSON.stringify({
          ...currentData,
          typeIdentite: e.target.value,
          'justificatif-identite-1-recto': emptyFile,
          'justificatif-identite-1-verso': emptyFile,
        })
      );
      setVersementJustificatifsData({
        ...versementJustificatifsData,
        typeDocumentIdentite: e.target.value,
        justificatifIdentiteRecto: '',
        justificatifIdentiteVerso: '',
      });
      setIsFormValid({ ...isFormValid, justificatifIdentiteVerso: true });
    } else if (versementJustificatifsData.typeDocumentIdentite === 'passeport') {
      sessionStorage.setItem(
        sessionStorageDataKey,
        JSON.stringify({
          ...currentData,
          typeIdentite: e.target.value,
          'justificatif-identite-1-recto': emptyFile,
          'justificatif-identite-1-verso': emptyFile,
        })
      );
      setVersementJustificatifsData({
        ...versementJustificatifsData,
        typeDocumentIdentite: e.target.value,
        justificatifIdentiteRecto: '',
        justificatifIdentiteVerso: '',
      });
    } else {
      sessionStorage.setItem(sessionStorageDataKey, JSON.stringify({ ...currentData, typeIdentite: e.target.value }));
      setVersementJustificatifsData({ ...versementJustificatifsData, typeDocumentIdentite: e.target.value });
    }
  };

  const checkFileForm = () => {
    if (
      versementJustificatifsData.typeDocumentIdentite === 'cni' ||
      versementJustificatifsData.typeDocumentIdentite === 'carte-de-sejour'
    ) {
      const hasJustificatifError =
        versementJustificatifError.pieceLu ||
        versementJustificatifError.pieceExpire ||
        versementJustificatifError.infosNonCorrespondante;

      if (
        ((validFile['justificatif-identite-1-recto'] !== true || hasJustificatifError) &&
          versementJustificatifsData.justificatifIdentiteRecto === '') ||
        ((validFile['justificatif-identite-1-verso'] !== true || hasJustificatifError) &&
          versementJustificatifsData.justificatifIdentiteVerso === '')
      ) {
        scrollToInputError('faire-versement-justif-anchor-pi');
        return true;
      }
    } else if (
      versementJustificatifsData.typeDocumentIdentite === 'passeport' &&
      (validFile['justificatif-identite-1-recto'] !== true ||
        validFile['justificatif-identite-1'] === undefined ||
        versementJustificatifError.pieceLu ||
        versementJustificatifError.pieceExpire ||
        versementJustificatifError.infosNonCorrespondante) &&
      versementJustificatifsData.justificatifIdentiteRecto === ''
    ) {
      scrollToInputError('faire-versement-justif-anchor-pi');
      return true;
    }
    if (
      showTelephone &&
      !validFile['justificatif-telephone'] &&
      versementJustificatifsData.justificatifTelephone === ''
    ) {
      scrollToInputError('faire-versement-justif-anchor-telephone');
      return true;
    }
    if (
      validFile['justificatif-origine_fonds'] === false &&
      versementJustificatifsData.justificatifOrigineFonds === '' &&
      showOrigineFonds
    ) {
      scrollToInputError('faire-versement-justif-anchor-origine-fonds');
      return true;
    }

    return false;
  };

  const sendToUniverSign = () => {
    setShowLoader(true);

    validationMutation.mutate(versementJustificatifsData.idSouscription, {
      onSuccess: (data) => {
        setShowLoader(false);

        if (data.success === true) {
          onSuccess?.(data);
        } else {
          setErrorJustificatif(true);
          setReadingErrorMessageId(
            data.reasonMessage === undefined ? null : getUniversignReadingErrorMessageId(data.reasonMessage)
          );

          if (data.pieceLu === false || data.pieceMRZ === true) {
            setDisplayErrorJustificatif(true);
            setVersementJustificatifError({
              pieceLu: true,
              infosNonCorrespondante: false,
              pieceExpire: false,
              coordonneesUtilisees: { mail: false, phone: false },
            });
          } else if (data.infosNonCorrespondante === true) {
            setDisplayErrorJustificatif(true);
            setVersementJustificatifError({
              pieceLu: false,
              infosNonCorrespondante: true,
              pieceExpire: false,
              coordonneesUtilisees: { mail: false, phone: false },
            });
            setBirthdate(data.birthdate ?? '');
          } else if (data.pieceExpire === true) {
            setDisplayErrorJustificatif(true);
            setVersementJustificatifError({
              pieceLu: false,
              infosNonCorrespondante: false,
              pieceExpire: true,
              coordonneesUtilisees: { mail: false, phone: false },
            });
          } else if (data.coordonneesUtilise === true) {
            if (data.emailUtilise === null && data.telephoneUtilise === null) {
              setVersementJustificatifError({
                pieceLu: false,
                infosNonCorrespondante: false,
                pieceExpire: false,
                coordonneesUtilisees: { mail: true, phone: true },
              });
              setShowContactConseillerButton(true);
            } else if (data.emailUtilise !== null) {
              setEmailTelUniversign(data.emailUtilise ?? '');
              setVersementJustificatifError({
                pieceLu: false,
                infosNonCorrespondante: false,
                pieceExpire: false,
                coordonneesUtilisees: { mail: true, phone: false },
              });
              setSubmitButtonLabel(t('common.validateButton'));
            } else if (data.telephoneUtilise !== null) {
              setEmailTelUniversign(data.telephoneUtilise ?? '');
              setVersementJustificatifError({
                pieceLu: false,
                infosNonCorrespondante: false,
                pieceExpire: false,
                coordonneesUtilisees: { mail: false, phone: true },
              });
            }
          }
          globalThis.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      onError: (error) => {
        navigate(ROUTE.ERREUR);
        log.error(`validationMutation failed: ${error}`);
      },
    });
  };

  const handleClick = (e: React.FormEvent) => {
    e.preventDefault();
    setUniversignTelMailError(false);
    setCoordonneesCheckErrorText('');

    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'placement',
      event_nom: 'aller_a_la_signature',
    });

    if (versementJustificatifError.coordonneesUtilisees.mail) {
      if (universignCoordonnees.email === '') {
        setUniversignTelMailError(true);
        setUniversignTelMailErrorText(t('justificatifsUniversign.requiredField'));
      } else {
        setShowLoader(true);
        handleSendVerificationCoordonnees(false);
      }
      globalThis.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    } else if (versementJustificatifError.coordonneesUtilisees.phone) {
      if (universignCoordonnees.telephone === '' || versementJustificatifsData.justificatifTelephone === '') {
        if (universignCoordonnees.telephone === '') {
          setUniversignTelMailError(true);
          setUniversignTelMailErrorText(t('justificatifsUniversign.requiredField'));
        } else if (isPhoneValid === false) {
          setUniversignTelMailError(true);
          setUniversignTelMailErrorText(t('justificatifsUniversign.invalidPhoneNumber'));
        }
        if (versementJustificatifsData.justificatifTelephone === '') setMissingValue(true);
        globalThis.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        handleSendVerificationCoordonnees(false);
      }
      globalThis.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setMissingValue(true);

    if (
      checkFileForm() ||
      !Object.values(isFormValid).every((item) => item === true) ||
      !Object.values(erreurCompression).every((item) => item === false)
    ) {
      return;
    }

    setMissingValue(false);
    sendToUniverSign();
  };

  const displayLabel = () => {
    return DOCUMENT_OPTIONS.find((o) => o.value === versementJustificatifsData.typeDocumentIdentite)?.label ?? '';
  };

  let pieceIdentiteInput = null;
  if (
    (validFile['justificatif-identite-1'] === undefined && !errorJustificatif) ||
    (errorJustificatif && versementJustificatifError.infosNonCorrespondante) ||
    (errorJustificatif && versementJustificatifError.pieceLu) ||
    (errorJustificatif && versementJustificatifError.pieceExpire)
  ) {
    if (versementJustificatifsData.typeDocumentIdentite === 'passeport') {
      pieceIdentiteInput = (
        <MtxGrid className={styles.documentInput} row>
          <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
            <InputFile
              formats={FORMATS_VALIDES}
              label={t('justificatifsUniversign.documentTypePasseport')}
              maxSize={TAILLE_MAX_MO}
              displayedMaxSize={4}
              blobUrl={
                justificatifsUrl.justificatifIdentiteRectoURL === ''
                  ? (parcoursData['justificatif-identite-1-recto']?.url ?? '')
                  : justificatifsUrl.justificatifIdentiteRectoURL
              }
              isError={displayErrorJustificatif || erreurCompression['justificatif-identite-1-recto']}
              errorMessageInvalid={errorMessageIdentiteRecto}
              whichFile="justificatif-identite-1-recto"
              disableInput={versementJustificatifsData.justificatifIdentiteRecto !== ''}
              setFile={setFile}
              handleFileValidity={handleFileValidity}
            />
            {missingValue && versementJustificatifsData.justificatifIdentiteRecto === '' && (
              <FormTextError text={t('justificatifsUniversign.requiredField')} />
            )}
          </MtxGrid>
        </MtxGrid>
      );
    } else {
      pieceIdentiteInput = (
        <>
          <MtxGrid className={styles.documentInput} row>
            <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
              <InputFile
                formats={FORMATS_VALIDES}
                label={`${displayLabel()} ${t('justificatifsUniversign.documentRecto')}`}
                maxSize={TAILLE_MAX_MO}
                displayedMaxSize={4}
                blobUrl={justificatifsUrl.justificatifIdentiteRectoURL}
                isError={displayErrorJustificatif || erreurCompression['justificatif-identite-1-recto']}
                errorMessageInvalid={errorMessageIdentiteRecto}
                whichFile="justificatif-identite-1-recto"
                disableInput={versementJustificatifsData.justificatifIdentiteRecto !== ''}
                setFile={setFile}
                handleFileValidity={handleFileValidity}
              />
              {missingValue && versementJustificatifsData.justificatifIdentiteRecto === '' && (
                <FormTextError text={t('justificatifsUniversign.requiredField')} />
              )}
            </MtxGrid>
          </MtxGrid>
          <MtxGrid className={styles.documentInput} row>
            <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
              <InputFile
                formats={FORMATS_VALIDES}
                label={`${displayLabel()} ${t('justificatifsUniversign.documentVerso')}`}
                maxSize={TAILLE_MAX_MO}
                displayedMaxSize={4}
                blobUrl={justificatifsUrl.justificatifIdentiteversoURL}
                isError={displayErrorJustificatif || erreurCompression['justificatif-identite-1-verso']}
                errorMessageInvalid={errorMessageIdentiteVerso}
                whichFile="justificatif-identite-1-verso"
                disableInput={versementJustificatifsData.justificatifIdentiteVerso !== ''}
                setFile={setFile}
                handleFileValidity={handleFileValidity}
              />
              {missingValue && versementJustificatifsData.justificatifIdentiteVerso === '' && (
                <FormTextError text={t('justificatifsUniversign.requiredField')} />
              )}
            </MtxGrid>
          </MtxGrid>
        </>
      );
    }
  }

  useEffect(() => {
    if (showOrigineFonds && origineCapitaux) {
      switch (origineCapitaux) {
        case 'revenu':
          setOrigineFondsLabel(t('justificatifsUniversign.origineFondsRevenuLabel'));
          setOrigineFondsInfo(t('justificatifsUniversign.origineFondsRevenuInfo'));
          break;
        case 'epargne':
          setOrigineFondsLabel(t('justificatifsUniversign.origineFondsEpargneLabel'));
          setOrigineFondsInfo(t('justificatifsUniversign.origineFondsEpargneInfo'));
          break;
        case 'gains-aux-jeux':
          setOrigineFondsLabel(t('justificatifsUniversign.origineFondsGainsJeuxLabel'));
          setOrigineFondsInfo(t('justificatifsUniversign.origineFondsGainsJeuxInfo'));
          break;
        case 'heritage-donation':
          setOrigineFondsLabel(t('justificatifsUniversign.origineFondsHeritageDonationLabel'));
          setOrigineFondsInfo(t('justificatifsUniversign.origineFondsHeritageDonationInfo'));
          break;
        case 'cession-actif':
          setOrigineFondsLabel(t('justificatifsUniversign.origineFondsCessionActifLabel'));
          setOrigineFondsInfo(t('justificatifsUniversign.origineFondsCessionActifInfo'));
          break;
        case 'autre-a-preciser':
          setOrigineFondsLabel(t('justificatifsUniversign.origineFondsAutreLabel'));
          setOrigineFondsInfo(t('justificatifsUniversign.origineFondsAutreInfo'));
          break;
      }
    }

    globalThis.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      {showModalRappel && (
        <ModaleDefaut
          content={<ModalRappel onClose={closeModal} data={rappel.data} />}
          onClickClose={closeModal}
          size="large"
          mobileStyle="defaut"
        />
      )}

      <MtxGrid className={styles.container}>
        {showLoader && <Loader />}

        {showModalCodeEmail && (
          <ModaleDefaut
            title={t('justificatifsUniversign.emailVerificationModalTitle')}
            content={modalCodeEmailContent}
            btnLabel={t('justificatifsUniversign.goToSignature')}
            btnVariant="primary"
            btnSize="large"
            onClickBtn={(e) => handleSubmitCodeVerification(e)}
            btnBottomLabel={t('common.cancel')}
            btnBottomVariant="tertiary"
            btnBottomSize="large"
            onClickBtnBottom={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            size="medium"
            mobileStyle="stick-to-bottom"
          />
        )}

        {showModalTelephone && (
          <ModaleDefaut
            title={t('modals.mobilephoneBillModal.title')}
            content={modalTelephoneContent}
            btnLabel={t('modals.mobilephoneBillModal.btnLabel')}
            btnVariant="primary"
            btnSize="large"
            onClickBtn={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            mobileStyle="stick-to-bottom"
            size="large"
          />
        )}

        {showModalBonnesPratiquesCni && (
          <ModaleDefaut
            title={t('justificatifsUniversign.bonnesPratiquesTitle')}
            content={modalCniContent}
            btnLabel={t('modals.mobilephoneBillModal.btnLabel')}
            btnVariant="primary"
            btnSize="large"
            onClickBtn={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            mobileStyle="defaut"
            size="large"
          />
        )}

        {showModalBonnesPratiquesPasseport && (
          <ModaleDefaut
            title={t('justificatifsUniversign.bonnesPratiquesTitle')}
            content={modalPasseportContent}
            btnLabel={t('modals.mobilephoneBillModal.btnLabel')}
            btnVariant="primary"
            btnSize="large"
            onClickBtn={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            mobileStyle="defaut"
            size="large"
          />
        )}

        {versementJustificatifError.coordonneesUtilisees.mail === true ? (
          <>
            {windowWidth < 900 ? (
              <button className="parrainage-content-back-arrow" onClick={handleErrorBreadcrumbs}>
                <i className="totem-icon totem-icon-arrow-left-big" />
              </button>
            ) : (
              <Breadcrumb items={breadcrumbItems} />
            )}

            {!errorJustificatif && <div className={styles.pageDivider}></div>}
          </>
        ) : (
          <MtxGrid className={styles.header} row>
            <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
              <Breadcrumb items={breadcrumbItems} />
              {validFile['justificatif-identite-1'] === undefined && !errorJustificatif && (
                <div>
                  <h1>{t('justificatifsUniversign.pageTitle')}</h1>
                  <p className={styles.headerText}>
                    <span className="bolder-text">{t('justificatifsUniversign.pageSubtitle1Bold')}</span>{' '}
                    {t('justificatifsUniversign.pageSubtitle1Rest')}
                  </p>
                  <p className={styles.headerTextDescription}>
                    <span className="bolder-text">{t('justificatifsUniversign.pageSubtitle2Bold')}</span>{' '}
                    {t('justificatifsUniversign.pageSubtitle2Rest')}
                  </p>
                </div>
              )}
            </MtxGrid>
          </MtxGrid>
        )}

        <div>
          {versementJustificatifError.pieceLu && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.universignError}>
                  <h3>
                    <i className="totem-icon totem-icon-cross-circle" />
                    {readingErrorMessageId
                      ? t(`justificatifsUniversign.readingErrors.${readingErrorMessageId}.title`)
                      : t('justificatifsUniversign.errorIdTitle')}
                  </h3>

                  {readingErrorMessageId ? (
                    <p className={styles.universignErrorText}>
                      {t(`justificatifsUniversign.readingErrors.${readingErrorMessageId}.text`)}
                    </p>
                  ) : (
                    <>
                      <p className={styles.universignErrorText}>{t('justificatifsUniversign.errorIdUnreadable')}</p>

                      <p className={styles.universignErrorBold}>
                        {t('justificatifsUniversign.errorIdUnreadableAdvice')}
                      </p>
                    </>
                  )}

                  <div className={styles.infoBox}>
                    <i className="totem-icon totem-icon-info" />
                    <p className={styles.universignErrorText}>
                      <button className={styles.infoBoxUnderline} onClick={() => openBonnesPratiquesModale()}>
                        {t('justificatifsUniversign.accessAdvice')}
                      </button>{' '}
                      {t('justificatifsUniversign.accessAdviceSuffix')}
                    </p>
                  </div>

                  <p className={styles.universignErrorText}>
                    {t('justificatifsUniversign.serviceClientIntro')}{' '}
                    <button
                      className={styles.universignErrorPhone}
                      onClick={() => handlePhoneCall()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePhoneCall();
                        }
                      }}
                      tabIndex={0}
                    >
                      {t('helpContact.tel')}
                    </button>
                    {DOT}
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}

          {versementJustificatifError.infosNonCorrespondante && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.universignError}>
                  <h3>
                    <i className="totem-icon totem-icon-cross-circle" />
                    {t('justificatifsUniversign.errorIdTitle')}
                  </h3>
                  <p className={styles.universignErrorText}>
                    {t('justificatifsUniversign.errorIdMismatch')}{' '}
                    <span className={styles.universignErrorBold}>
                      {user?.nom}, {user?.prenom}, le {birthdate.split('-').reverse().join('/')}
                    </span>
                  </p>

                  <p className={styles.universignErrorBold}>{t('justificatifsUniversign.errorIdMismatchAdvice')}</p>

                  <p className={styles.universignErrorText}>
                    {t('justificatifsUniversign.serviceClientIntro')}{' '}
                    <button
                      className={styles.universignErrorPhone}
                      onClick={() => handlePhoneCall()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePhoneCall();
                        }
                      }}
                      tabIndex={0}
                    >
                      {t('helpContact.tel')}
                    </button>
                    {DOT}
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}

          {versementJustificatifError.coordonneesUtilisees.mail &&
            versementJustificatifError.coordonneesUtilisees.phone === false && (
              <MtxGrid row>
                <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                  <div className={styles.universignError}>
                    <h3>
                      <i className="totem-icon totem-icon-cross-circle" />
                      {t('justificatifsUniversign.errorSignatureTitle')}
                    </h3>

                    <p className={styles.universignErrorText}>
                      {t('justificatifsUniversign.errorSignatureEmailUsed')}{' '}
                      <span className={styles.universignErrorBold}>{emailTelUniversign}</span>
                    </p>

                    <p className={styles.universignErrorBold}>
                      {t('justificatifsUniversign.errorSignatureEmailAdvice')}
                    </p>

                    <form
                      onSubmit={(e) => {
                        handleClick(e);
                      }}
                    >
                      <TextInput
                        label={t('justificatifsUniversign.emailLabel')}
                        labelFor="universign-error-email"
                        placeholder={t('justificatifsUniversign.emailPlaceholder')}
                        type="email"
                        isError={universignTelMailError}
                        textError={universignTelMailErrorText}
                        onChange={(e) => {
                          setUniversignCoordonnees({ ...universignCoordonnees, email: e.currentTarget.value });
                          setUniversignTelMailError(false);
                          setCoordonneesCheckErrorText('');
                        }}
                      />
                      <input className="hide-element" type="submit" />
                    </form>

                    <p className={styles.universignErrorText}>
                      {t('justificatifsUniversign.serviceClientIntro')}{' '}
                      <button
                        className={styles.universignErrorPhone}
                        onClick={() => handlePhoneCall()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePhoneCall();
                          }
                        }}
                        tabIndex={0}
                      >
                        {t('helpContact.tel')}
                      </button>
                      {DOT}
                    </p>
                  </div>

                  {coordonneesCheckErrorText && (
                    <div className={styles.coordonneesCheckError}>
                      <i className="totem-icon totem-icon-cross-circle" />
                      <p>{coordonneesCheckErrorText}</p>
                    </div>
                  )}
                </MtxGrid>
              </MtxGrid>
            )}

          {versementJustificatifError.coordonneesUtilisees.mail === false &&
            versementJustificatifError.coordonneesUtilisees.phone && (
              <MtxGrid row>
                <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                  <div className={styles.universignError}>
                    <h3>
                      <i className="totem-icon totem-icon-cross-circle" />
                      {t('justificatifsUniversign.errorSignatureTitle')}
                    </h3>

                    <p className={styles.universignErrorText}>
                      {t('justificatifsUniversign.errorSignaturePhoneUsed')}{' '}
                      <span className={styles.universignErrorBold}>{emailTelUniversign}</span>
                    </p>

                    <p className={styles.universignErrorBold}>
                      {t('justificatifsUniversign.errorSignaturePhoneAdvice')}
                    </p>

                    <label className="text-input-top-label">
                      {t('justificatifsUniversign.phoneLabel')}
                      <IntlTelInput
                        initialCountry="fr"
                        separateDialCode={false}
                        showFlags={true}
                        strictMode={true}
                        formatAsYouType={true}
                        formatOnDisplay={true}
                        fixDropdownWidth={true}
                        nationalMode={false}
                        onChangeNumber={(value) => OnChangePhoneInputError(value)}
                        onChangeValidity={(isValid) => setIsPhoneValid(isValid)}
                        inputProps={{
                          className:
                            'rappel-tel-input-container ' +
                            (universignTelMailError && 'rappel-tel-input-container-error'),
                          name: 'phone',
                          placeholder: t('justificatifsUniversign.phonePlaceholder'),
                          required: true,
                        }}
                      />
                      {universignTelMailError && <FormTextError text={universignTelMailErrorText} />}
                    </label>

                    <p className={styles.universignErrorText}>
                      {t('justificatifsUniversign.serviceClientIntro')}{' '}
                      <button
                        className={styles.universignErrorPhone}
                        onClick={() => handlePhoneCall()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePhoneCall();
                          }
                        }}
                        tabIndex={0}
                      >
                        {t('helpContact.tel')}
                      </button>
                      {DOT}
                    </p>
                  </div>

                  {coordonneesCheckErrorText && (
                    <div className={styles.coordonneesCheckError}>
                      <i className="totem-icon totem-icon-cross-circle" />
                      <p>{coordonneesCheckErrorText}</p>
                    </div>
                  )}
                </MtxGrid>
              </MtxGrid>
            )}

          {versementJustificatifError.coordonneesUtilisees.mail &&
            versementJustificatifError.coordonneesUtilisees.phone && (
              <MtxGrid row>
                <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                  <div className={styles.universignError}>
                    <h3>
                      <i className="totem-icon totem-icon-cross-circle" />
                      {t('justificatifsUniversign.errorSignatureTitle')}
                    </h3>

                    <p className={styles.universignErrorText}>
                      {showContactAdvisorFallback
                        ? t('justificatifsUniversign.errorSignatureTechnical')
                        : t('justificatifsUniversign.errorSignatureBothUsed')}
                    </p>

                    <p className={styles.universignErrorBold}>
                      {t('justificatifsUniversign.errorSignatureBothAdvicePrefix')}{' '}
                      <button
                        className={styles.universignErrorPhone}
                        onClick={() => handlePhoneCall()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePhoneCall();
                          }
                        }}
                        tabIndex={0}
                      >
                        {t('helpContact.tel')}
                      </button>{' '}
                      {t('justificatifsUniversign.errorSignatureBothAdviceSuffix')}
                    </p>
                  </div>
                </MtxGrid>
              </MtxGrid>
            )}

          {versementJustificatifError.pieceExpire && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.universignError}>
                  <h3>
                    <i className="totem-icon totem-icon-cross-circle" />
                    {t('justificatifsUniversign.errorIdTitle')}
                  </h3>

                  <p className={styles.universignErrorText}>{t('justificatifsUniversign.errorIdExpired')}</p>

                  <p className={styles.universignErrorBold}>{t('justificatifsUniversign.errorIdExpiredAdvice')}</p>

                  <p className={styles.universignErrorText}>
                    {t('justificatifsUniversign.serviceClientIntro')}{' '}
                    <button
                      className={styles.universignErrorPhone}
                      onClick={() => handlePhoneCall()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePhoneCall();
                        }
                      }}
                      tabIndex={0}
                    >
                      {t('helpContact.tel')}
                    </button>
                    {DOT}
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}
          <hr className={styles.LineDivider} />
          {!(
            (versementJustificatifError.coordonneesUtilisees.mail &&
              versementJustificatifError.coordonneesUtilisees.phone) ||
            versementJustificatifError.coordonneesUtilisees.mail
          ) && <h3 className={styles.sectionTitle}>{t('justificatifsUniversign.sectionTitle')}</h3>}

          {((validFile['justificatif-identite-1'] === undefined && !errorJustificatif) ||
            (errorJustificatif && versementJustificatifError.infosNonCorrespondante) ||
            (errorJustificatif && versementJustificatifError.pieceLu) ||
            (errorJustificatif && versementJustificatifError.pieceExpire)) && (
            <MtxGrid row>
              <MtxGrid
                className={styles.idLabelContainer}
                col={12}
                colXs={12}
                colSm={12}
                colMd={12}
                colLg={12}
                colXl={12}
              >
                <label className={styles.idLabel} htmlFor="choix-piece-identite">
                  {t('justificatifsUniversign.documentTypeLabel')}
                </label>
              </MtxGrid>

              <MtxGrid className={styles.selectField} col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <select
                  id="choix-piece-identite"
                  className={styles.idSelect}
                  value={versementJustificatifsData.typeDocumentIdentite}
                  onChange={(e) => {
                    handleSelectChange(e);
                  }}
                  required
                >
                  {DOCUMENT_OPTIONS.map((option) => (
                    <option className="" key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </MtxGrid>
            </MtxGrid>
          )}

          {((validFile['justificatif-identite-1'] === undefined && !errorJustificatif) ||
            (errorJustificatif && versementJustificatifError.infosNonCorrespondante) ||
            (errorJustificatif && versementJustificatifError.pieceExpire)) && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.infoBoxPage}>
                  <i className="totem-icon totem-icon-info" />
                  <p>
                    <button className={styles.infoBoxUnderline} onClick={() => openBonnesPratiquesModale()}>
                      {t('justificatifsUniversign.accessAdvice')}
                    </button>{' '}
                    {t('justificatifsUniversign.accessAdviceSuffix')}
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}

          <div className="scroll-anchor" id="faire-versement-justif-anchor-pi"></div>

          {pieceIdentiteInput}

          {((showTelephone && validFile['justificatif-telephone'] === undefined && !errorJustificatif) ||
            (errorJustificatif &&
              versementJustificatifError.coordonneesUtilisees.phone === true &&
              versementJustificatifError.coordonneesUtilisees.mail === false)) && (
            <MtxGrid className={styles.documentInput} row>
              <div className="scroll-anchor" id="faire-versement-justif-anchor-telephone"></div>
              <MtxGrid className={styles.phoneContainer} col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <button
                  type="button"
                  className={styles.phoneInfoButton}
                  onClick={() => openModal('showModalTelephone')}
                >
                  <i className="totem-icon totem-icon-info" />
                </button>
                <InputFile
                  formats={FORMATS_VALIDES}
                  label={t('justificatifsUniversign.phoneBill')}
                  maxSize={TAILLE_MAX_MO}
                  displayedMaxSize={4}
                  blobUrl={justificatifsUrl.justificatifTelephoneURL}
                  isError={erreurCompression['justificatif-telephone']}
                  errorMessageInvalid={
                    erreurCompression['justificatif-telephone']
                      ? messageErreurFichier['justificatif-telephone'] || t('justificatifsUniversign.fileUploadError')
                      : ''
                  }
                  whichFile="justificatif-telephone"
                  disableInput={versementJustificatifsData.justificatifTelephone !== ''}
                  setFile={setFile}
                  handleFileValidity={handleFileValidity}
                />

                {missingValue && versementJustificatifsData.justificatifTelephone === '' && (
                  <FormTextError text={t('justificatifsUniversign.requiredField')} />
                )}
              </MtxGrid>
            </MtxGrid>
          )}

          {showOrigineFonds && (
            <div className={styles.fundFileInputs}>
              <div>
                <div className="scroll-anchor" id="faire-versement-justif-anchor-origine-fonds"></div>
                <h3>{t('justificatifsUniversign.origineFondsTitle')}</h3>

                <MtxGrid row>
                  <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                    <InputFile
                      formats={FORMATS_VALIDES}
                      label={origineFondsLabel}
                      maxSize={TAILLE_MAX_MO}
                      displayedMaxSize={4}
                      blobUrl={justificatifsUrl.justificatifOrigineFondsURL}
                      isError={erreurCompression['justificatif-origine_fonds']}
                      errorMessageInvalid={
                        erreurCompression['justificatif-origine_fonds']
                          ? messageErreurFichier['justificatif-origine_fonds'] ||
                            t('justificatifsUniversign.fileUploadError')
                          : ''
                      }
                      whichFile="justificatif-origine_fonds"
                      disableInput={versementJustificatifsData.justificatifOrigineFonds !== ''}
                      setFile={setFile}
                      handleFileValidity={handleFileValidity}
                    />

                    {missingValue && versementJustificatifsData.justificatifOrigineFonds === '' && (
                      <FormTextError text={t('justificatifsUniversign.requiredField')} />
                    )}
                  </MtxGrid>
                </MtxGrid>

                <MtxGrid row>
                  <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                    <div className={`${styles.infoBox} ${styles.infoBoxLast}`}>
                      <i className="totem-icon totem-icon-info" />
                      <p>{origineFondsInfo}</p>
                    </div>
                  </MtxGrid>
                </MtxGrid>
              </div>
            </div>
          )}

          {!(
            (versementJustificatifError.coordonneesUtilisees.mail &&
              versementJustificatifError.coordonneesUtilisees.phone) ||
            versementJustificatifError.coordonneesUtilisees.mail
          ) && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <span className={styles.mandatoryText}>{t('justificatifsUniversign.mandatoryMentions')}</span>
              </MtxGrid>
            </MtxGrid>
          )}

          <MtxGrid row>
            <MtxGrid className={styles.confirmButton} col={4} colXs={12} colSm={6} colMd={6} colLg={4} colXl={4}>
              {showContactConseillerButton ? (
                <>
                  <MtxButton
                    className="tchatclose1"
                    onClick={() => openModalRappel()}
                    variant="primary"
                    size="medium"
                    fullWidth={true}
                  >
                    <div>{t('justificatifsUniversign.contactAdvisor')}</div>
                  </MtxButton>

                  <MtxButton
                    className="tchatopen1"
                    style={{ display: 'none' }}
                    variant="primary"
                    size="medium"
                    fullWidth={true}
                    onClick={() => {}}
                  >
                    <div>{t('justificatifsUniversign.contactAdvisor')}</div>
                  </MtxButton>
                </>
              ) : (
                <MtxButton
                  onClick={handleClick}
                  variant="primary"
                  size="large"
                  fullWidth={true}
                  disabled={disableSubmitBtn}
                >
                  <div>{submitButtonLabel}</div>
                </MtxButton>
              )}
            </MtxGrid>
          </MtxGrid>
        </div>
      </MtxGrid>
    </>
  );
};

export default UniversignDocumentsForm;
